/**
 * MCC / Construction Loan auto-start writes `status: "completed"` at the end of
 * Step 1 (identify) and Step 2 (assign), with `workflow_step` 1 or 2.
 * Only Step 3 completion means the whole workflow is done.
 */

export type MccUiStepStatus = 'pending' | 'running' | 'complete' | 'error';

export type MccWorkflowProgress = {
  step?: unknown;
  status?: unknown;
  workflow_step?: unknown;
  is_active?: unknown;
  progress?: unknown;
  progress_percentage?: unknown;
  /** Server blobs carry more (last_updated, failed_step, retryable, ...). */
  [key: string]: unknown;
} | null;

export type MccWorkflowStepStatuses = {
  step0: MccUiStepStatus;
  step1: MccUiStepStatus;
  step2: MccUiStepStatus;
};

const RUNNING_STATUSES = new Set(['running', 'in_progress', 'in-progress']);
const COMPLETE_STATUSES = new Set(['complete', 'completed']);
const ERROR_STATUSES = new Set(['error', 'failed']);

export function normalizeWorkflowStatus(status: unknown): string {
  return String(status ?? '').toLowerCase();
}

export function workflowStepNumber(
  progress: MccWorkflowProgress,
  fallback?: number | null,
): number | null {
  const raw = progress?.workflow_step ?? fallback;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

export function isCrossCheckWorkflowError(status: unknown): boolean {
  return ERROR_STATUSES.has(normalizeWorkflowStatus(status));
}

function stepKeyOf(progress: MccWorkflowProgress): string {
  return String(progress?.step ?? '').toLowerCase();
}

function isActiveFlag(progress: MccWorkflowProgress): boolean {
  return progress?.is_active === true;
}

function progressPercent(progress: MccWorkflowProgress): number | null {
  if (typeof progress?.progress === 'number') return progress.progress;
  if (typeof progress?.progress_percentage === 'number') {
    return progress.progress_percentage as number;
  }
  return null;
}

function isRunningStatus(status: string): boolean {
  return RUNNING_STATUSES.has(status);
}

/**
 * True when the full auto-start workflow has finished or failed.
 * Mid-workflow Step 1/2 `status: "completed"` is not terminal.
 */
export function isCrossCheckWorkflowTerminal(
  progress: MccWorkflowProgress,
  fallbackStatus?: string | null,
): boolean {
  const status = normalizeWorkflowStatus(progress?.status ?? fallbackStatus);
  if (isCrossCheckWorkflowError(status)) return true;

  const stepKey = stepKeyOf(progress);
  if (stepKey === 'step3_completed') return true;

  const workflowStep = workflowStepNumber(progress);
  if (!COMPLETE_STATUSES.has(status)) return false;
  if (workflowStep === 3) return true;
  if (workflowStep === 1 || workflowStep === 2) return false;
  if (stepKey === 'step1_completed' || stepKey === 'step2_completed') return false;
  // Legacy payloads: `completed` without a step number means the run is done.
  return true;
}

export function isCrossCheckWorkflowComplete(
  progress: MccWorkflowProgress,
  fallbackStatus?: string | null,
  fallbackStep?: number | null,
): boolean {
  if (isCrossCheckWorkflowError(progress?.status ?? fallbackStatus)) return false;

  const status = normalizeWorkflowStatus(progress?.status ?? fallbackStatus);
  const stepKey = stepKeyOf(progress);
  const workflowStep = workflowStepNumber(progress, fallbackStep);
  const isActive = isActiveFlag(progress);
  const progNum = progressPercent(progress);

  if (stepKey === 'step3_completed') return true;
  if (COMPLETE_STATUSES.has(status) && workflowStep === 3) return true;
  if (stepKey === 'complete' && !isActive && workflowStep === 3) return true;
  if (
    workflowStep === 3 &&
    !isActive &&
    (progNum == null || progNum >= 100) &&
    !isRunningStatus(status) &&
    (COMPLETE_STATUSES.has(status) || status === '' || status === 'pending')
  ) {
    return true;
  }
  return false;
}

export function resolveMccWorkflowStepStatuses(input: {
  loading?: boolean;
  progress: MccWorkflowProgress;
  workflowStatus?: string | null;
  workflowStep?: number | null;
  hasIdentifiedClauses?: boolean;
  hasCrossCheckResults?: boolean;
}): MccWorkflowStepStatuses {
  if (input.loading) {
    return { step0: 'complete', step1: 'pending', step2: 'pending' };
  }

  const progress = input.progress;
  const status = normalizeWorkflowStatus(progress?.status ?? input.workflowStatus);
  const workflowStep = workflowStepNumber(progress, input.workflowStep);
  const isActive = isActiveFlag(progress);
  const hasIdentifiedClauses = Boolean(input.hasIdentifiedClauses);
  const hasCrossCheckResults = Boolean(input.hasCrossCheckResults);

  const workflowError = isCrossCheckWorkflowError(status);
  const workflowComplete = isCrossCheckWorkflowComplete(
    progress,
    input.workflowStatus,
    input.workflowStep,
  );

  // Finished project with results but no / incomplete progress blob.
  // Never use partial Step-3 writes (or saved-for-later cards) as "done"
  // while Step 1–3 are still in flight.
  const resultsLookFinished =
    hasCrossCheckResults &&
    !isActive &&
    !isRunningStatus(status) &&
    (workflowStep == null || workflowStep >= 3) &&
    !['step1_completed', 'step2_completed', 'step1_running', 'step2_running', 'step3_running'].includes(
      stepKeyOf(progress),
    );

  const workflowStarted =
    Boolean(progress && Object.keys(progress).length > 0) ||
    workflowStep != null ||
    isActive ||
    ['running', 'in_progress', 'in-progress', 'complete', 'completed', 'error', 'failed'].includes(
      status,
    );

  const step0: MccUiStepStatus = 'complete';

  if (!workflowStarted) {
    return { step0, step1: 'pending', step2: 'pending' };
  }

  if (workflowError) {
    return {
      step0,
      step1: workflowStep != null && workflowStep >= 2 ? 'complete' : 'running',
      step2: 'error',
    };
  }

  if (workflowComplete || resultsLookFinished) {
    return { step0, step1: 'complete', step2: 'complete' };
  }

  if ((workflowStep != null && workflowStep >= 2) || hasIdentifiedClauses) {
    return { step0, step1: 'complete', step2: 'running' };
  }

  return { step0, step1: 'running', step2: 'pending' };
}

/** Which step a run is waiting on, when its clause cards are being held back. */
export type ClauseCardsHoldStage = 'identify' | 'cross_check';

/**
 * Which step, if any, is holding the clause cards back from the user.
 *
 * Nothing worth showing exists until Step 2 finishes. Before Step 1 starts, and
 * while it runs, there is only an empty tab bar and a 0/0 progress line. Step 1
 * then publishes the identified clauses, but their bank of changes and past
 * negotiations arrive with Step 2: shown in between, every card renders as a red
 * "missing negotiation history", which reads as a failed analysis rather than one
 * still in progress. In every one of those states the user is better served by
 * being told what is running.
 *
 * The cards are released once Step 2 completes — and also when it fails, since
 * partial results are worth seeing next to the error. The error check comes first
 * because a Step 1 failure leaves step1 itself marked running.
 *
 * `startDeclined` covers the one case where both steps are pending and no run is
 * coming: the user was offered the cross-check and chose "later". A progress blob
 * that has not been written yet looks exactly the same from here, so the caller
 * has to say which it is; without it the user would face a spinner for a run that
 * is never going to start.
 */
export function clauseCardsHoldStage(
  statuses: MccWorkflowStepStatuses,
  options: { startDeclined?: boolean } = {},
): ClauseCardsHoldStage | null {
  if (statuses.step2 === 'error' || statuses.step2 === 'complete') return null;
  if (statuses.step2 === 'running') return 'cross_check';
  if (statuses.step1 === 'running') return 'identify';
  // Both pending: a run spinning up (no progress blob written yet), unless the
  // user declined to start one.
  return options.startDeclined ? null : 'identify';
}

/**
 * The parts of a progress blob whose change is worth a full results reload.
 *
 * Pollers read the tiny progress endpoint every few seconds and re-fetch the
 * full sub-project (multi-MB once results exist) only when this signature
 * changes. `last_updated` is deliberately excluded: the server heartbeat
 * bumps it every 30s while a run is alive, which is not a change to render.
 */
export function crossCheckProgressSignature(progress: MccWorkflowProgress): string {
  if (!progress) return 'none';
  const p = progress as Record<string, unknown>;
  return JSON.stringify([
    p.status,
    p.workflow_step,
    p.step,
    p.progress,
    p.current_step_name,
    p.is_active,
    p.failed_step,
  ]);
}
